import React, { useState } from 'react';
import { Info, Eye, EyeOff, Shield, Building, UserCheck, Users } from 'lucide-react';

// getLevelLabel: texto do nível conforme o formulário do cliente (ver
// routingOptionLabel); sem ele, vale o mapa fixo dos slugs legados abaixo.
const PermissionTooltip = ({ user, getLevelLabel, children }) => {
  const [isVisible, setIsVisible] = useState(false);

  const getLevelInfo = (level) => {
    const levelMap = {
      'admin_cliente1': {
        label: 'Diretores/Vice-Presidência',
        icon: <Building className="h-4 w-4" />,
        color: 'text-purple-600',
        description: 'Pode ver denúncias sobre diretores e vice-presidentes'
      },
      'admin_cliente2': {
        label: 'Presidência',
        icon: <UserCheck className="h-4 w-4" />,
        color: 'text-red-600',
        description: 'Pode ver denúncias sobre presidência'
      },
      'admin_cliente3': {
        label: 'Demais Colaboradores',
        icon: <Users className="h-4 w-4" />,
        color: 'text-green-600',
        description: 'Pode ver denúncias sobre demais colaboradores'
      }
    };
    
    return levelMap[level] || {
      label: 'Nível Desconhecido',
      icon: <Users className="h-4 w-4" />,
      color: 'text-gray-600',
      description: 'Nível de acesso não definido'
    };
  };

  const getUserPermissions = () => {
    if (user.is_admin) {
      return {
        type: 'Super Admin',
        color: 'text-red-600',
        icon: <Shield className="h-4 w-4" />,
        permissions: [
          'Ver todos os formulários',
          'Ver todas as denúncias',
          'Gerenciar usuários',
          'Acessar logs de auditoria',
          'Configurar sistema'
        ],
        restrictions: []
      };
    }

    if (user.is_client_admin) {
      let adminLevels = [];
      
      // Tratar diferentes formatos de admin_levels
      if (user.admin_levels) {
        if (typeof user.admin_levels === 'string') {
          try {
            // Tentar fazer parse como JSON primeiro
            adminLevels = JSON.parse(user.admin_levels);
          } catch (e) {
            // Se não for JSON válido, tratar como string simples
            adminLevels = [user.admin_levels];
          }
        } else if (Array.isArray(user.admin_levels)) {
          adminLevels = user.admin_levels;
        }
      }
      
      const levelInfos = adminLevels.map(level => {
        const info = getLevelInfo(level);
        const label = getLevelLabel && getLevelLabel(level);
        return label ? { ...info, label, description: `Pode ver denúncias sobre ${label}` } : info;
      });
      
      return {
        type: 'Admin do Cliente',
        color: 'text-blue-600',
        icon: <Building className="h-4 w-4" />,
        permissions: [
          `Ver formulários do cliente ${user.client_name}`,
          `Ver dashboard do cliente ${user.client_name}`,
          `Gerenciar usuários do cliente ${user.client_name}`,
          'Acessar logs de auditoria do cliente',
          ...levelInfos.map(info => `Ver denúncias sobre ${info.label}`)
        ],
        restrictions: [
          'Não pode ver formulários de outros clientes',
          'Não pode ver denúncias de outros clientes',
          'Não pode configurar sistema global',
          'Não pode acessar logs globais'
        ],
        levels: levelInfos
      };
    }

    if (user.is_investigador_externo) {
      return {
        type: 'Investigador Externo',
        color: 'text-orange-600',
        icon: <Eye className="h-4 w-4" />,
        permissions: [
          'Ver denúncias atribuídas',
          'Acessar evidências',
          'Adicionar comentários',
          'Atualizar status das investigações'
        ],
        restrictions: [
          'Não pode ver outras denúncias',
          'Não pode gerenciar usuários',
          'Não pode acessar configurações'
        ]
      };
    }

    return {
      type: 'Usuário Comum',
      color: 'text-gray-600',
      icon: <UserCheck className="h-4 w-4" />,
      permissions: [
        'Ver suas próprias denúncias',
        'Acessar formulários públicos'
      ],
      restrictions: [
        'Não pode ver outras denúncias',
        'Não pode gerenciar usuários',
        'Não pode acessar dashboard administrativo'
      ]
    };
  };

  const permissions = getUserPermissions();

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className="cursor-help"
      >
        {children}
      </div>

      {isVisible && (
        <div className="absolute z-50 w-80 bg-white border border-gray-200 rounded-lg shadow-lg p-4 transform -translate-x-1/2 left-1/2 -top-2">
          {/* Seta */}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-200"></div>
          
          {/* Header */}
          <div className="flex items-center mb-3 pb-2 border-b border-gray-100">
            <div className={`${permissions.color} mr-2`}>
              {permissions.icon}
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{permissions.type}</h3>
              <p className="text-sm text-gray-500">{user.username}</p>
            </div>
          </div>

          {/* Cliente */}
          {user.client_name && (
            <div className="mb-3">
              <p className="text-sm font-medium text-gray-700">Cliente:</p>
              <p className="text-sm text-gray-600">{user.client_name}</p>
            </div>
          )}

          {/* Níveis de Acesso (para client_admin) */}
          {permissions.levels && permissions.levels.length > 0 && (
            <div className="mb-3">
              <p className="text-sm font-medium text-gray-700 mb-2">Níveis de Acesso:</p>
              <div className="space-y-1">
                {permissions.levels.map((level, index) => (
                  <div key={index} className="flex items-center text-sm">
                    <div className={`${level.color} mr-2`}>
                      {level.icon}
                    </div>
                    <span className="text-gray-600">{level.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Permissões */}
          <div className="mb-3">
            <p className="text-sm font-medium text-gray-700 mb-2 flex items-center">
              <Eye className="h-4 w-4 mr-1 text-green-600" />
              Pode:
            </p>
            <ul className="text-sm text-gray-600 space-y-1">
              {permissions.permissions.map((permission, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  {permission}
                </li>
              ))}
            </ul>
          </div>

          {/* Restrições */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2 flex items-center">
              <EyeOff className="h-4 w-4 mr-1 text-red-600" />
              Não pode:
            </p>
            <ul className="text-sm text-gray-600 space-y-1">
              {permissions.restrictions.map((restriction, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-red-500 mr-2">✗</span>
                  {restriction}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default PermissionTooltip;
